import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Plus, Save, Copy, Pencil, Droplets,
  Heart, Handshake, Gift, Sparkles, Ticket, Palette, AlertTriangle, Trash2, LayoutTemplate
} from "lucide-react";
import { LayoutThumbnail } from "./LayoutThumbnail";

const TYPE_ICONS = { lavado: Droplets, cortesia: Heart, atencion_comercial: Handshake, cheque_regalo: Gift, personalizado: Sparkles };
const TYPE_LABELS = { lavado: "Lavado", cortesia: "Cortesia", atencion_comercial: "Atencion comercial", cheque_regalo: "Cheque regalo", personalizado: "Personalizado" };

export default function VoucherTemplates() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDuplicate, setConfirmDuplicate] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axiosAuth().get("/vouchers/templates");
      setTemplates(r.data.templates || []);
    } catch { toast.error("Error al cargar plantillas"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditData({
      isNew: true, name: "", type: "lavado", title: "BONO DE LAVADO GRATUITO",
      subtitle: "", description_text: "", conditions: "",
      default_expiry_days: 30, default_value: 15, bar_color: "#0f172a",
      accent_color: "#dc2626", active: true,
    });
    setEditDialog(true);
  };

  const openEdit = (tpl) => {
    setEditData({ ...tpl, isNew: false });
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editData.name.trim()) { toast.error("Nombre obligatorio"); return; }
    setSaving(true);
    try {
      if (editData.isNew) {
        await axiosAuth().post("/vouchers/templates", editData);
        toast.success("Plantilla creada");
      } else {
        await axiosAuth().put(`/vouchers/templates/${editData.id}`, editData);
        toast.success("Plantilla actualizada");
      }
      setEditDialog(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleDuplicate = async (tpl) => {
    try {
      await axiosAuth().post(`/vouchers/templates/${tpl.id}/duplicate`);
      toast.success("Plantilla duplicada");
      setConfirmDuplicate(null);
      await load();
    } catch { toast.error("Error al duplicar"); }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteDialog) return;
    if (deleteConfirmText !== "ELIMINAR") { toast.error("Escribe ELIMINAR para confirmar"); return; }
    setDeleteLoading(true);
    try {
      await axiosAuth().delete(`/vouchers/templates/${deleteDialog.id}`);
      toast.success("Plantilla eliminada");
      setDeleteDialog(null); setDeleteConfirmText("");
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || "Error al eliminar";
      toast.error(detail);
    } finally { setDeleteLoading(false); }
  };

  const upd = (field, value) => setEditData(d => ({ ...d, [field]: value }));

  if (loading) return <div className="p-8 text-center text-sm text-slate-400">Cargando plantillas...</div>;

  return (
    <div className="space-y-6" data-testid="voucher-templates">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase tracking-tight">Plantillas</h1>
          <p className="text-sm text-slate-500 mt-1">{templates.length} plantilla{templates.length !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={openCreate} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm text-xs uppercase tracking-wider h-9 px-5 font-semibold" data-testid="new-tpl-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Nueva plantilla
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(tpl => {
          const Icon = TYPE_ICONS[tpl.type] || Ticket;
          return (
            <Card key={tpl.id} className={`rounded-sm overflow-hidden transition-all hover:shadow-md group ${tpl.active ? "border-slate-200 hover:border-slate-300" : "border-slate-100 opacity-60"}`} data-testid={`tpl-card-${tpl.id}`}>
              <div className="relative">
                <LayoutThumbnail layout={tpl.layout} barColor={tpl.bar_color} accentColor={tpl.accent_color} className="w-full border-b border-slate-100" />
                <div className="absolute top-2 right-2">
                  {tpl.active ? (
                    <Badge variant="outline" className="h-5 text-[9px] rounded-sm border-emerald-200 text-emerald-600 bg-emerald-50/90 backdrop-blur-sm font-semibold shadow-sm">Activa</Badge>
                  ) : (
                    <Badge variant="outline" className="h-5 text-[9px] rounded-sm border-slate-200 text-slate-400 bg-white/90 backdrop-blur-sm font-semibold shadow-sm">Inactiva</Badge>
                  )}
                </div>
                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/5 transition-colors" />
              </div>
              <CardContent className="p-4 space-y-2.5">
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${tpl.accent_color}12`, color: tpl.accent_color }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate leading-tight">{tpl.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-bold">{TYPE_LABELS[tpl.type] || tpl.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-400 font-medium">
                  <span>{tpl.default_expiry_days} dias</span>
                  <span className="text-slate-200">|</span>
                  <span className="font-mono">{tpl.default_value}€</span>
                  <span className="text-slate-200">|</span>
                  <span>v{tpl.version}</span>
                </div>
                <div className="flex gap-1.5 pt-0.5">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/admin/atenciones/plantillas/editor/${tpl.id}`)} className="flex-1 rounded-sm text-[10px] uppercase h-8 tracking-wider font-semibold bg-slate-900 text-white hover:bg-slate-800 hover:text-white border-slate-900" data-testid={`visual-editor-tpl-${tpl.id}`}>
                    <LayoutTemplate className="w-3 h-3 mr-1.5" /> Editor visual
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEdit(tpl)} className="h-8 w-8 p-0 rounded-sm" data-testid={`edit-tpl-${tpl.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDuplicate(tpl)} className="h-8 w-8 p-0 rounded-sm text-slate-400 hover:text-slate-700" data-testid={`dup-tpl-${tpl.id}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setDeleteDialog(tpl); setDeleteConfirmText(""); }}
                    className="h-8 w-8 p-0 rounded-sm text-slate-300 hover:text-red-600 hover:bg-red-50" data-testid={`del-tpl-${tpl.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={o => { if (!o) setEditDialog(false); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-sm" data-testid="tpl-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <Palette className="w-5 h-5 text-red-600" />
              {editData?.isNew ? "Nueva plantilla" : `Editar: ${editData?.name}`}
            </DialogTitle>
          </DialogHeader>
          {editData && (
            <div className="space-y-4 py-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Nombre interno *</Label>
                  <Input value={editData.name} onChange={e => upd("name", e.target.value)}
                    placeholder="Bono de lavado estandar" className="rounded-sm h-9 border-slate-200 mt-1" data-testid="tpl-name-input" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Tipo</Label>
                  <Select value={editData.type} onValueChange={v => upd("type", v)}>
                    <SelectTrigger className="rounded-sm h-9 border-slate-200 mt-1" data-testid="tpl-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Titulo principal</Label>
                <Input value={editData.title} onChange={e => upd("title", e.target.value)}
                  placeholder="BONO DE LAVADO GRATUITO" className="rounded-sm h-9 border-slate-200 font-bold uppercase mt-1" data-testid="tpl-title-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Subtitulo</Label>
                <Input value={editData.subtitle || ""} onChange={e => upd("subtitle", e.target.value)}
                  placeholder="Valido para un lavado completo" className="rounded-sm h-9 border-slate-200 mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Texto descriptivo</Label>
                <Textarea value={editData.description_text || ""} onChange={e => upd("description_text", e.target.value)}
                  className="rounded-sm border-slate-200 mt-1" rows={2} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Condiciones</Label>
                <Textarea value={editData.conditions || ""} onChange={e => upd("conditions", e.target.value)}
                  className="rounded-sm border-slate-200 mt-1" rows={2} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Dias validez</Label>
                  <Input type="number" value={editData.default_expiry_days} onChange={e => upd("default_expiry_days", parseInt(e.target.value) || 30)}
                    className="rounded-sm h-9 border-slate-200 mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Valor (EUR)</Label>
                  <Input type="number" step="0.01" value={editData.default_value} onChange={e => upd("default_value", parseFloat(e.target.value) || 0)}
                    className="rounded-sm h-9 border-slate-200 mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Color barra</Label>
                  <div className="flex gap-1.5 mt-1">
                    <input type="color" value={editData.bar_color || "#0f172a"} onChange={e => upd("bar_color", e.target.value)}
                      className="w-9 h-9 rounded-sm border border-slate-200 cursor-pointer" />
                    <Input value={editData.bar_color || "#0f172a"} onChange={e => upd("bar_color", e.target.value)}
                      className="rounded-sm h-9 border-slate-200 font-mono text-xs flex-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Color acento</Label>
                  <div className="flex gap-1.5 mt-1">
                    <input type="color" value={editData.accent_color || "#dc2626"} onChange={e => upd("accent_color", e.target.value)}
                      className="w-9 h-9 rounded-sm border border-slate-200 cursor-pointer" />
                    <Input value={editData.accent_color || "#dc2626"} onChange={e => upd("accent_color", e.target.value)}
                      className="rounded-sm h-9 border-slate-200 font-mono text-xs flex-1" />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600 mb-2 block">Vista previa</Label>
                <div className="border border-slate-200 rounded-sm overflow-hidden max-w-md shadow-sm">
                  <div className="h-1.5" style={{ backgroundColor: editData.bar_color || "#0f172a" }} />
                  <div className="h-0.5" style={{ backgroundColor: editData.accent_color || "#dc2626" }} />
                  <div className="p-4 space-y-2">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.15em]">ASCAUTO</p>
                    <p className="text-base font-bold uppercase tracking-tight" style={{ color: editData.bar_color || "#0f172a" }}>{editData.title || "TITULO"}</p>
                    {editData.subtitle && <p className="text-[10px] text-slate-500">{editData.subtitle}</p>}
                    <div className="h-px bg-slate-200" />
                    <div className="grid grid-cols-2 gap-2 text-[9px]">
                      <div><span className="text-slate-400 font-bold">CLIENTE:</span> <span className="text-slate-700">Juan Ejemplo</span></div>
                      <div><span className="text-slate-400 font-bold">MATRICULA:</span> <span className="font-mono text-slate-700">1234ABC</span></div>
                    </div>
                    <div className="bg-slate-50 p-2 rounded-sm text-[8px] text-slate-400">{editData.conditions || "Condiciones..."}</div>
                  </div>
                  <div className="h-0.5" style={{ backgroundColor: editData.accent_color || "#dc2626" }} />
                  <div className="h-1" style={{ backgroundColor: editData.bar_color || "#0f172a" }} />
                </div>
              </div>

              <div className="flex items-center justify-between border border-slate-200 rounded-sm p-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Plantilla activa</Label>
                  <p className="text-[10px] text-slate-400 mt-0.5">Si esta desactivada no aparecera al emitir bonos.</p>
                </div>
                <Switch checked={editData.active} onCheckedChange={v => upd("active", v)} data-testid="tpl-active-toggle" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="save-tpl-btn">
              <Save className="w-3 h-3 mr-1.5" /> {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate confirmation */}
      <Dialog open={!!confirmDuplicate} onOpenChange={o => { if (!o) setConfirmDuplicate(null); }}>
        <DialogContent className="max-w-sm rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Duplicar plantilla
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              Se creara una copia de la plantilla &quot;{confirmDuplicate?.name}&quot;. Podras editarla despues.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDuplicate(null)} className="rounded-sm">Cancelar</Button>
            <Button onClick={() => handleDuplicate(confirmDuplicate)}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm">
              Si, duplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete template confirmation (double: write ELIMINAR) */}
      <Dialog open={!!deleteDialog} onOpenChange={o => { if (!o) setDeleteDialog(null); }}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="delete-tpl-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" /> Eliminar plantilla
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              Vas a eliminar la plantilla <strong>&quot;{deleteDialog?.name}&quot;</strong>.
              Si tiene bonos asociados, el sistema no permitira la eliminacion y sugerira desactivarla.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-red-600">Escribe ELIMINAR para confirmar</Label>
            <Input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="ELIMINAR" className="rounded-sm h-9 border-red-200 mt-1 font-mono uppercase" data-testid="delete-tpl-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="rounded-sm">Cancelar</Button>
            <Button onClick={handleDeleteTemplate} disabled={deleteLoading || deleteConfirmText !== "ELIMINAR"}
              className="bg-red-600 text-white hover:bg-red-700 rounded-sm disabled:opacity-40" data-testid="confirm-delete-tpl-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {deleteLoading ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
